import React, { useState, useEffect } from 'react';
import LocationSearchInputBackend from './LocationSearchInputBackend';
import MapPicker from './MapPicker';

interface CombinedLocationSelectorProps {
  value: string;
  onChange: (value: string, placeDetails?: any) => void;
  placeholder?: string;
  onCurrentLocationClick?: () => void;
  initialLocation?: { lat: number; lng: number } | null;
  onLocationSelect?: (location: { lat: number; lng: number; address: string }) => void;
  onConfirm: () => void;
  confirmButtonText?: string;
  stepTitle?: string;
  address?: string;
  middleContent?: React.ReactNode;
}

const CombinedLocationSelector: React.FC<CombinedLocationSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Enter location',
  onCurrentLocationClick,
  initialLocation,
  onLocationSelect,
  onConfirm,
  confirmButtonText = 'Confirm Location',
  stepTitle,
  address,
  middleContent,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(
    initialLocation || null
  );
  const [showMap, setShowMap] = useState(false);
  const [locationAddress, setLocationAddress] = useState(value || address || '');

  useEffect(() => {
    setLocationAddress(value || address || '');
  }, [value, address]);

  useEffect(() => {
    if (initialLocation) {
      setSelectedLocation(initialLocation);
      setShowMap(true);
    }
  }, [initialLocation]);

  const handlePlaceSelected = (value: string, placeDetails?: any) => {
    onChange(value, placeDetails);
    setLocationAddress(value);
    
    // If placeDetails has geometry.location (from LocationSearchInputBackend)
    if (placeDetails?.geometry?.location) {
      // Handle both function format (lat(), lng()) and direct format
      const lat = typeof placeDetails.geometry.location.lat === 'function' 
        ? placeDetails.geometry.location.lat() 
        : placeDetails.geometry.location.lat;
      const lng = typeof placeDetails.geometry.location.lng === 'function' 
        ? placeDetails.geometry.location.lng() 
        : placeDetails.geometry.location.lng;
      
      const loc = {
        lat,
        lng,
      };
      setSelectedLocation(loc);
      setShowMap(true);
      // Also call onLocationSelect if provided
      if (onLocationSelect) {
        onLocationSelect({
          lat: loc.lat,
          lng: loc.lng,
          address: value
        });
      }
    } else if (placeDetails?.location) {
      // Handle direct location format
      const loc = {
        lat: typeof placeDetails.location.lat === 'function' 
          ? placeDetails.location.lat() 
          : placeDetails.location.lat,
        lng: typeof placeDetails.location.lng === 'function' 
          ? placeDetails.location.lng() 
          : placeDetails.location.lng,
      };
      setSelectedLocation(loc);
      setShowMap(true);
      if (onLocationSelect) {
        onLocationSelect({
          lat: loc.lat,
          lng: loc.lng,
          address: value
        });
      }
    }
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setSelectedLocation({ lat: location.lat, lng: location.lng });
    setLocationAddress(location.address);
    onChange(location.address, { location: { lat: location.lat, lng: location.lng } });
    onLocationSelect?.(location);
  };

  const handleConfirm = () => {
    if (selectedLocation && locationAddress) {
      onConfirm();
    }
  };

  return (
    <div className="space-y-4">
      {stepTitle && (
        <div className="bg-white border border-gray-300 rounded-md p-4">
          <h3 className="font-semibold text-black">{stepTitle}</h3>
        </div>
      )}

      {/* Search Section */}
      <div className="space-y-2">
        <LocationSearchInputBackend
          value={locationAddress}
          onChange={handlePlaceSelected}
          placeholder={placeholder}
          onCurrentLocationClick={onCurrentLocationClick}
        />
      </div>

      {/* Middle Content - Rendered between search and map */}
      {middleContent && (
        <div className="space-y-2">
          {middleContent}
        </div>
      )}

      {/* Map Section - Show when location is selected */}
      {showMap && selectedLocation ? (
        <div className="space-y-2">
          <MapPicker
            initialLocation={selectedLocation}
            onLocationSelect={handleLocationSelect}
            address={locationAddress}
          />
        </div>
      ) : (
        <div className="bg-gray-100 border border-gray-300 rounded-md p-8 text-center">
          <p className="text-gray-500">Search for a location to see it on the map</p>
        </div>
      )}

      {/* Confirm Button */}
      <button
        onClick={handleConfirm}
        disabled={!selectedLocation || !locationAddress}
        className={`w-full flex items-center justify-center px-6 py-3 rounded-md transition-colors ${
          selectedLocation && locationAddress
            ? 'bg-black text-white hover:bg-black700'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
        }`}
      >
        {confirmButtonText}
      </button>
    </div>
  );
};

export default CombinedLocationSelector;

